import { createContext, useContext, useState, useEffect, useLayoutEffect, ReactNode, useCallback } from "react";
import { Language, TranslationKeys } from "./types";
import { translations } from "./translations";

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
};

const LanguageContext = createContext<LanguageContextType | null>(null);

const STORAGE_KEY = "gooffline-lang";
const SUPPORTED_LANGS: Language[] = ["en", "fr", "de", "pl"];

const detectLanguage = (): Language => {
  if (typeof window === "undefined") return "en";

  // Check localStorage first
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_LANGS.includes(stored as Language)) {
    return stored as Language;
  }

  // Check browser language
  const browserLang = navigator.language.split("-")[0];
  if (SUPPORTED_LANGS.includes(browserLang as Language)) {
    return browserLang as Language;
  }

  return "en";
};

/**
 * Check if the page was pre-rendered at build time.
 * Pre-rendered pages are always in English, so we must start with "en"
 * during hydration to avoid React hydration mismatch errors (#418, #423, #425).
 */
const isPrerendered = (): boolean =>
  typeof document !== "undefined" &&
  (document.getElementById("root")?.hasAttribute("data-prerendered") ?? false);

type LanguageProviderProps = {
  children: ReactNode;
};

export const LanguageProvider = ({ children }: LanguageProviderProps): JSX.Element => {
  const [language, setLanguageState] = useState<Language>(() => {
    // During hydration of pre-rendered pages, always start with "en"
    // to match the server-rendered HTML and prevent hydration mismatches.
    if (isPrerendered()) return "en";
    return detectLanguage();
  });
  
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, []);
  
  // After hydration, detect and apply the user's preferred language.
  // useLayoutEffect runs synchronously before the browser paints,
  // preventing a visible flash of English content for non-English users.
  useLayoutEffect(() => {
    const detected = detectLanguage();
    if (detected !== language) {
      setLanguageState(detected);
      document.documentElement.lang = detected;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  
  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
  };
  
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

